/**
 * @file ImportFieldDropdown.js
 * @description A dropdown component for selecting an import field based on the import type.
 */

import { useState, useEffect } from 'react';
import { getImportFields } from '../../../services/importService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const ImportFieldDropdown = ({
    className,
    inlineLabel = true,
    selectedField,
    onFieldChange,
    importTypeId,
    label = '',
}) => {
    const [fields, setFields] = useState([]);
    const [defaultLabel, setDefaultLabel] = useState('Select Field');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchImportFields = async () => {
            try {
                const importFieldsResponse =
                    await getImportFields(importTypeId);

                setFields(importFieldsResponse);

                const matchingField =
                    selectedField &&
                    importFieldsResponse.find(
                        field => field.id === selectedField.id,
                    );
                setDefaultLabel(
                    matchingField ? matchingField.name : 'Select Field',
                );
            } catch (error) {
                console.error('Could not fetch import fields:', error);
                Toast.error(getText('msg_616abdfc683c'));
            } finally {
                setIsLoading(false);
            }
        };

        if (importTypeId) {
            fetchImportFields();
        } else {
            setFields([]);
            setDefaultLabel('Select Field');
            setIsLoading(false);
        }
    }, [importTypeId]);

    useEffect(() => {
        if (selectedField && fields.length > 0) {
            const matchingField = fields.find(
                field => field.id === selectedField.id,
            );
            setDefaultLabel(
                matchingField ? matchingField.name : 'Select Field',
            );
        } else {
            setDefaultLabel('Select Field');
        }
    }, [selectedField, fields]);

    const fieldOptions = [
        { value: null, label: getText('msg_444b96c44241') },
        ...fields.map(field => ({
            value: { id: field.id, name: field.name },
            label: field.name,
        })),
    ];

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <>
                {label && (
                    <label htmlFor="importField">
                        {label}
                        {/* <span className="text-danger">*</span> */}
                    </label>
                )}
                <LoadingWrapper isLoading={isLoading}>
                    <Dropdown
                        defaultValue={defaultLabel}
                        options={fieldOptions}
                        onChange={value => onFieldChange(value)}
                        value={selectedField}
                        disabled={fields.length === 0 || isLoading}
                    />
                </LoadingWrapper>
            </>
        </div>
    );
};

export default ImportFieldDropdown;
